import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { DEPLOYMENT_STATUS_STYLES } from '../../constants';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalModel, ModelType } from '../../types';
import { fmtIso } from '../../utils';

interface RayModelListPageProps {
  modelType: 'OD' | 'PR';
  title: string;
  description?: string;
}

export const RayModelListPage: React.FC<RayModelListPageProps> = ({
  modelType,
  title,
  description,
}) => {
  const { token } = useAuthContext();
  const [models, setModels] = useState<EvalModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [updatingEmbeddings, setUpdatingEmbeddings] = useState(false);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const showEmbeddings = modelType === 'PR';

  useEffect(() => {
    EvalApiService.listModels(token ?? '', { model_type: modelType }).then(({ items }) => {
      setModels(items);
      setLoading(false);
    });
  }, [token, modelType]);

  const filterByType = (items: EvalModel[]) =>
    items.filter((m) => m.model_type === (modelType as ModelType));

  const handleSync = () => {
    setSyncing(true);
    EvalApiService.syncModels(token ?? '')
      .then(({ items }) => {
        setModels(filterByType(items));
        setLastSynced(new Date());
      })
      .finally(() => setSyncing(false));
  };

  const handleUpdateEmbeddings = () => {
    if (
      !window.confirm(
        'Counting embeddings is intensive on MongoDB. Are you sure you want to continue?'
      )
    )
      return;
    setUpdatingEmbeddings(true);
    EvalApiService.updateEmbeddingCounts(token ?? '')
      .then(({ items }) => setModels(filterByType(items)))
      .finally(() => setUpdatingEmbeddings(false));
  };

  const filtered = models.filter((m) => !statusFilter || m.deployment_status === statusFilter);

  return (
    <div className="page-content">
      {updatingEmbeddings && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              border: '4px solid rgba(255,255,255,0.2)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Counting embeddings…</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
            This may take up to a minute
          </div>
        </div>
      )}

      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">{title}</div>
          <div className="eval-page-sub">
            {description ? `${description} ` : ''}
            {lastSynced ? `Last synced ${fmtIso(lastSynced.toISOString())}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {showEmbeddings && (
            <button
              className="eval-btn-ghost"
              onClick={handleUpdateEmbeddings}
              disabled={updatingEmbeddings || syncing}
            >
              {updatingEmbeddings ? 'Updating…' : 'Update Embedding Count'}
            </button>
          )}
          <button
            className="eval-btn-ghost"
            onClick={handleSync}
            disabled={syncing || updatingEmbeddings}
          >
            {syncing ? 'Syncing…' : 'Sync Ray Models'}
          </button>
        </div>
      </div>

      <div className="eval-filters">
        <select
          className="eval-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="live">Live</option>
          <option value="staging">Staging</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Deployment Status</th>
              {showEmbeddings && <th>Embeddings</th>}
              <th>Registered At</th>
              <th>Updated At</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.model_id}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                  <Link to={`/eval/models/${m.model_id}`} style={{ color: 'var(--accent)' }}>
                    {m.version}
                  </Link>
                </td>
                <td>
                  <span style={{ ...DEPLOYMENT_STATUS_STYLES[m.deployment_status], fontSize: 12 }}>
                    {m.deployment_status.charAt(0).toUpperCase() + m.deployment_status.slice(1)}
                  </span>
                </td>
                {showEmbeddings && (
                  <td>
                    {m.embedding_count != null ? (
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 12,
                          padding: '1px 6px',
                          borderRadius: 4,
                          background: m.embedding_count === 0 ? 'var(--danger)' : 'transparent',
                          color: m.embedding_count === 0 ? '#fff' : 'var(--text-primary)',
                        }}
                      >
                        {m.embedding_count.toLocaleString()}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                )}
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fmtIso(m.registered_at)}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.updated_at ? fmtIso(m.updated_at) : '—'}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.description ?? '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={showEmbeddings ? 6 : 5}
                  style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}
                >
                  No {modelType} models match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export const OdModelListPage: React.FC = () => (
  <RayModelListPage
    modelType="OD"
    title="OD Models"
    description="Object Detection models synced from Ray."
  />
);

export const PrModelListPage: React.FC = () => (
  <RayModelListPage
    modelType="PR"
    title="PR Models"
    description="Product Recognition models synced from Ray."
  />
);
